import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { recentMoodEntries } from './moodDates.js';
import ModalDialog from './ModalDialog.jsx';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, detectUnconstructiveContent, containsProfanity, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function TeamMoodOverview({ allUsers }) {
  const [moodData, setMoodData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const moodEmojis = { 1: '😞', 2: '😕', 3: '😐', 4: '😊', 5: '😄' };
  const moodLabels = { 1: 'Not awesome', 2: 'Could be better', 3: 'Okay', 4: 'Pretty good', 5: 'Great!' };
  const moodColors = {
    1: 'bg-red-100 text-red-700 border-red-200',
    2: 'bg-orange-100 text-orange-700 border-orange-200',
    3: 'bg-amber-100 text-amber-700 border-amber-200',
    4: 'bg-green-100 text-green-700 border-green-200',
    5: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  };
  const moodBarColors = { 1: 'bg-red-400', 2: 'bg-orange-400', 3: 'bg-amber-400', 4: 'bg-green-400', 5: 'bg-emerald-400' };

  useEffect(() => {
    const loadAll = async () => {
      const data = {};
      for (const u of allUsers) {
        try {
          const raw = await storage.get(`mood:${u.id}`, false);
          if (raw) data[u.id] = { user: u, checkins: JSON.parse(raw) };
        } catch { /* skip */ }
      }
      setMoodData(data);
    };
    if (allUsers.length > 0) loadAll();
  }, [allUsers]);

  if (!moodData) return null;

  const today = new Date().toISOString().split('T')[0];
  const usersWithData = Object.values(moodData);

  // Today's summary
  const todayCheckins = usersWithData.filter(d => d.checkins[today]);
  const todayAvg = todayCheckins.length > 0
    ? (todayCheckins.reduce((s, d) => s + d.checkins[today].rating, 0) / todayCheckins.length).toFixed(1)
    : null;
  const todayDist = [0, 0, 0, 0, 0];
  todayCheckins.forEach(d => { todayDist[d.checkins[today].rating - 1]++; });

  // Selected date data
  const selectedCheckins = usersWithData
    .filter(d => d.checkins[selectedDate])
    .map(d => ({ ...d.user, rating: d.checkins[selectedDate].rating, timestamp: d.checkins[selectedDate].timestamp }))
    .sort((a, b) => a.rating - b.rating);
  const notCheckedIn = allUsers.filter(u => !moodData[u.id]?.checkins[selectedDate]);

  // 7-day trend
  const last7 = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const ds = d.toISOString().split('T')[0];
    const dayCheckins = usersWithData.filter(x => x.checkins[ds]);
    const avg = dayCheckins.length > 0
      ? dayCheckins.reduce((s, x) => s + x.checkins[ds].rating, 0) / dayCheckins.length
      : null;
    last7.push({ date: ds, avg, count: dayCheckins.length, label: d.toLocaleDateString(undefined, { weekday: 'short' }) });
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-stone-200 mb-6 overflow-hidden">
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-stone-100">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{todayAvg ? (todayAvg >= 4 ? '😊' : todayAvg >= 3 ? '😐' : '😕') : '📊'}</span>
          <div>
            <h3 className="font-semibold text-stone-800">Team Mood Overview</h3>
            <p className="text-xs text-stone-500">
              {todayCheckins.length}/{allUsers.length} checked in today
              {todayAvg && <span> · Avg: {todayAvg}/5</span>}
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
        >
          {expanded ? 'Collapse' : 'View Details'}
        </button>
      </div>

      {/* Today's distribution bar */}
      {todayCheckins.length > 0 && (
        <div className="px-4 py-3 flex items-center gap-3">
          <span className="text-xs text-stone-500 w-12 flex-shrink-0">Today</span>
          <div className="flex-1 flex h-6 rounded-full overflow-hidden">
            {todayDist.map((count, idx) => count > 0 && (
              <div
                key={idx}
                className={`${moodBarColors[idx + 1]} flex items-center justify-center text-white text-xs font-medium`}
                style={{ width: `${(count / todayCheckins.length) * 100}%` }}
                title={`${moodEmojis[idx + 1]} ${moodLabels[idx + 1]}: ${count}`}
              >
                {count > 0 && <span>{moodEmojis[idx + 1]}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 7-day trend sparkline */}
      <div className="px-4 pb-3 flex items-end gap-1">
        {last7.map(day => (
          <button
            key={day.date}
            onClick={() => { setSelectedDate(day.date); if (!expanded) setExpanded(true); }}
            className={`flex-1 flex flex-col items-center gap-1 py-1 rounded transition-colors ${
              selectedDate === day.date && expanded ? 'bg-amber-50' : 'hover:bg-stone-50'
            }`}
            title={`${day.date}: ${day.avg ? day.avg.toFixed(1) + '/5' : 'No data'} (${day.count} check-ins)`}
          >
            {day.avg !== null ? (
              <div
                className={`w-full rounded-sm ${
                  day.avg >= 4 ? 'bg-green-400' :
                  day.avg >= 3 ? 'bg-amber-400' :
                  day.avg >= 2 ? 'bg-orange-400' : 'bg-red-400'
                }`}
                style={{ height: `${day.avg * 6}px` }}
              />
            ) : (
              <div className="w-full h-1 rounded-sm bg-stone-200" />
            )}
            <span className={`text-[10px] ${day.date === today ? 'font-bold text-amber-700' : 'text-stone-500'}`}>
              {day.label}
            </span>
          </button>
        ))}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-stone-100">
          {/* Date selector */}
          <div className="px-4 py-3 bg-stone-50 flex items-center gap-3">
            <span className="text-xs font-medium text-stone-600">Date:</span>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              max={today}
              className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
            />
            {selectedDate !== today && (
              <button
                onClick={() => setSelectedDate(today)}
                className="text-xs text-amber-700 hover:text-amber-800 font-medium"
              >
                Jump to today
              </button>
            )}
          </div>

          {/* User mood list for selected date */}
          <div className="max-h-72 overflow-y-auto divide-y divide-stone-50">
            {selectedCheckins.length === 0 && notCheckedIn.length === allUsers.length ? (
              <p className="text-stone-500 text-sm text-center py-6">No check-ins recorded for this date</p>
            ) : (
              <>
                {selectedCheckins.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50">
                    <span className="text-xl">{moodEmojis[u.rating]}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-800 truncate">{u.name}</div>
                      <div className="text-xs text-stone-500 truncate">{u.email}</div>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${moodColors[u.rating]}`}>
                      {moodLabels[u.rating]}
                    </span>
                  </div>
                ))}
                {notCheckedIn.length > 0 && (
                  <div className="px-4 py-3 bg-stone-50">
                    <p className="text-xs font-medium text-stone-500 mb-2">
                      Not checked in ({notCheckedIn.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {notCheckedIn.map(u => (
                        <span key={u.id} className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                          {u.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// === DAILY MOOD CHECK-IN MODAL ===
function MoodCheckInModal({ user, onSubmit, onDismiss }) {
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const moods = [
    { rating: 1, emoji: '😞', label: 'Not awesome at all', color: 'from-red-600 to-red-700', bg: 'bg-red-50 border-red-200 hover:border-red-400', activeBg: 'bg-red-100 border-red-500 ring-2 ring-red-300' },
    { rating: 2, emoji: '😕', label: 'Could be better', color: 'from-orange-700 to-orange-800', bg: 'bg-orange-50 border-orange-200 hover:border-orange-400', activeBg: 'bg-orange-100 border-orange-500 ring-2 ring-orange-300' },
    { rating: 3, emoji: '😐', label: 'Doing okay', color: 'from-amber-700 to-yellow-700', bg: 'bg-amber-50 border-amber-200 hover:border-amber-400', activeBg: 'bg-amber-100 border-amber-500 ring-2 ring-amber-300' },
    { rating: 4, emoji: '😊', label: 'Pretty good', color: 'from-lime-700 to-green-700', bg: 'bg-green-50 border-green-200 hover:border-green-400', activeBg: 'bg-green-100 border-green-500 ring-2 ring-green-300' },
    { rating: 5, emoji: '😄', label: 'Great!', color: 'from-emerald-700 to-teal-700', bg: 'bg-emerald-50 border-emerald-200 hover:border-emerald-400', activeBg: 'bg-emerald-100 border-emerald-500 ring-2 ring-emerald-300' },
  ];

  const handleSelect = (rating) => {
    setSelected(rating);
    // Show optional reason field for all mood ratings
    setShowDetails(true);
  };

  const handleSubmit = async () => {
    if (selected === null) return;
    if (details.trim()) {
      if (containsProfanity(details)) {
        alert('Please remove profanity from your response');
        return;
      }
      const toneCheck = detectUnconstructiveContent(details);
      if (toneCheck.isUnconstructive) {
        alert(toneCheck.reason || 'Please keep your response constructive.');
        return;
      }
    }
    setSubmitting(true);
    await onSubmit(selected, details.trim() || null);
    setSubmitting(false);
  };

  return (
    <ModalDialog onClose={onDismiss} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" aria-label="Daily mood check-in" onClick={onDismiss}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90dvh] overflow-y-auto" onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-700 to-orange-700 p-6 text-center" style={{ flexShrink: 0 }}>
          <div className="text-4xl mb-2">{selected !== null ? moods.find(m => m.rating === selected)?.emoji : '👋'}</div>
          <h2 className="text-xl font-medium text-white">How are you feeling today?</h2>
          <p className="text-amber-50 text-sm mt-1">Hi {user.name?.split(' ')[0]}, take a moment to check in</p>
        </div>

        {/* Mood Options */}
        <div className="p-6" style={{ overflowY: 'auto', minHeight: 0 }}>
          <div className="space-y-2">
            {moods.map(mood => (
              <button
                key={mood.rating}
                aria-pressed={selected === mood.rating}
                onClick={() => handleSelect(mood.rating)}
                className={`w-full flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${
                  selected === mood.rating ? mood.activeBg : mood.bg
                }`}
              >
                <span className="text-3xl">{mood.emoji}</span>
                <div className="flex-1 text-left">
                  <div className={`font-medium ${selected === mood.rating ? 'text-stone-900' : 'text-stone-700'}`}>
                    {mood.label}
                  </div>
                </div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  selected === mood.rating
                    ? `bg-gradient-to-br ${mood.color} text-white shadow-md`
                    : 'bg-stone-100 text-stone-600'
                }`}>
                  {mood.rating}
                </div>
              </button>
            ))}
          </div>

          {/* Optional reason for any mood rating */}
          {showDetails && selected !== null && (
            <div className={`mt-4 p-4 ${selected <= 2 ? 'bg-amber-50 border border-amber-200' : 'bg-stone-50 border border-stone-200'} rounded-xl`}>
              <p className={`text-sm font-medium ${selected <= 2 ? 'text-amber-800' : 'text-stone-700'} mb-2`}>
                {selected <= 2 
                  ? "We're sorry you're not feeling your best. Is there something specific contributing to how you're feeling?"
                  : "Would you like to share what's on your mind? (Optional)"}
              </p>
              <p className={`text-xs ${selected <= 2 ? 'text-amber-700' : 'text-stone-500'} mb-3`}>Adding a note is optional.</p>
              <textarea
                aria-label="Optional mood note"
                value={details}
                onChange={e => setDetails(e.target.value)}
                placeholder={selected <= 2 ? "e.g., Stress from the conflict, tough day at work, feeling unheard..." : "e.g., Had a great conversation, feeling optimistic about resolution..."}
                maxLength={500}
                rows={3}
                className={`w-full px-3 py-2 border ${selected <= 2 ? 'border-amber-300' : 'border-stone-300'} rounded-lg text-sm text-stone-700 placeholder-stone-500 focus:ring-2 focus:ring-amber-400 focus:outline-none resize-none bg-white`}
              />
              <div className="mt-1">
                <span className={`text-xs ${selected <= 2 ? 'text-amber-700' : 'text-stone-500'}`}>{details.length}/500</span>
              </div>
            </div>
          )}

        </div>
          <div className="flex gap-3" style={{ flexShrink: 0, padding: '12px 24px', background: 'white' }}>
            <button
              onClick={onDismiss}
              className="flex-1 py-3 text-stone-600 hover:bg-stone-100 rounded-xl font-medium transition-colors text-sm"
            >
              Remind me later
            </button>
            <button
              onClick={handleSubmit}
              disabled={selected === null || submitting}
              className="flex-1 py-3 bg-amber-700 hover:bg-amber-800 disabled:bg-stone-200 disabled:text-stone-500 text-white rounded-xl font-medium transition-all"
            >
              {submitting ? 'Saving...' : 'Submit'}
            </button>
          </div>
      </div>
    </ModalDialog>
  );
}

// === MOOD HISTORY WIDGET (for Dashboard) ===
function MoodHistoryWidget({ user }) {
  const [checkins, setCheckins] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await storage.get(`mood:${user.id}`, false);
        setCheckins(data ? JSON.parse(data) : {});
      } catch { setCheckins({}); }
    };
    load();
  }, [user.id]);

  if (checkins === null) return null;

  const entries = Object.entries(checkins)
    .sort(([a], [b]) => b.localeCompare(a))
    .slice(0, expanded ? 30 : 7);

  const moodEmojis = { 1: '😞', 2: '😕', 3: '😐', 4: '😊', 5: '😄' };
  const moodLabels = { 1: 'Not awesome', 2: 'Could be better', 3: 'Okay', 4: 'Pretty good', 5: 'Great!' };
  const moodColors = { 1: 'bg-red-100 text-red-700', 2: 'bg-orange-100 text-orange-700', 3: 'bg-amber-100 text-amber-700', 4: 'bg-green-100 text-green-700', 5: 'bg-emerald-100 text-emerald-700' };

  const today = new Date().toISOString().split('T')[0];
  const todayEntry = checkins[today];
  const recent = recentMoodEntries(checkins);
  const recentAvg = recent.length > 0
    ? (recent.reduce((sum, [, e]) => sum + e.rating, 0) / recent.length).toFixed(1)
    : null;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      {/* Compact header with today's mood */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{todayEntry ? moodEmojis[todayEntry.rating] : '📊'}</span>
          <div>
            <div className="text-sm font-medium text-stone-800">
              {todayEntry ? `Today: ${moodLabels[todayEntry.rating]}` : 'Mood Tracker'}
            </div>
            {recentAvg && (
              <div className="text-xs text-stone-500">
                {entries.length}-day avg: {recentAvg}/5
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-amber-700 hover:text-amber-800 font-medium"
        >
          {expanded ? 'Less' : 'History'}
        </button>
      </div>

      {/* Recent streak / sparkline */}
      {entries.length > 1 && (
        <div className="px-4 pb-3 flex items-end gap-1">
          {entries.slice().reverse().slice(-14).map(([date, entry], idx) => (
            <div key={date} className="flex-1 flex flex-col items-center gap-1" title={`${date}: ${moodLabels[entry.rating]}`}>
              <div
                className={`w-full rounded-sm ${
                  entry.rating >= 4 ? 'bg-green-400' :
                  entry.rating === 3 ? 'bg-amber-400' :
                  entry.rating === 2 ? 'bg-orange-400' : 'bg-red-400'
                }`}
                style={{ height: `${entry.rating * 6}px` }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Expanded history */}
      {expanded && (
        <div className="border-t border-stone-100 max-h-60 overflow-y-auto">
          {entries.length === 0 ? (
            <p className="text-stone-500 text-sm text-center py-4">No check-ins yet</p>
          ) : (
            entries.map(([date, entry]) => (
              <div key={date} className="flex items-center gap-3 px-4 py-2 hover:bg-stone-50">
                <span className="text-lg">{moodEmojis[entry.rating]}</span>
                <div className="flex-1">
                  <span className="text-sm text-stone-700">
                    {date === today ? 'Today' : new Date(date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  {entry.details && (
                    <p className="text-xs text-stone-500 mt-0.5 italic">"{entry.details}"</p>
                  )}
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${moodColors[entry.rating]}`}>
                  {moodLabels[entry.rating]}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// === PARTICIPANT MOOD OVERVIEW (Dashboard — Mentor/Observer/Global/Admin) ===
function ParticipantMoodOverview({ user, conflicts }) {
  const [moodData, setMoodData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const moodEmojis = { 1: '😞', 2: '😕', 3: '😐', 4: '😊', 5: '😄' };
  const moodLabels = { 1: 'Not awesome', 2: 'Could be better', 3: 'Okay', 4: 'Pretty good', 5: 'Great!' };
  const moodColors = {
    1: 'bg-red-100 text-red-700',
    2: 'bg-orange-100 text-orange-700',
    3: 'bg-amber-100 text-amber-700',
    4: 'bg-green-100 text-green-700',
    5: 'bg-emerald-100 text-emerald-700'
  };

  useEffect(() => {
    const load = async () => {
      // Collect unique participant IDs from all conflicts this user can see
      const participantMap = {};
      for (const c of conflicts) {
        (c.mentees || []).forEach(m => {
          if (m.id !== user.id) participantMap[m.id] = m;
        });
      }
      const data = {};
      for (const [id, p] of Object.entries(participantMap)) {
        try {
          const raw = await storage.get(`mood:${id}`, false);
          if (raw) data[id] = { user: p, checkins: JSON.parse(raw) };
        } catch { /* skip */ }
      }
      setMoodData(data);
    };
    load();
  }, [conflicts, user.id]);

  if (!moodData || Object.keys(moodData).length === 0) return null;

  const today = new Date().toISOString().split('T')[0];
  const entries = Object.values(moodData);
  const todayCheckins = entries.filter(d => d.checkins[today]);
  const todayAvg = todayCheckins.length > 0
    ? (todayCheckins.reduce((s, d) => s + d.checkins[today].rating, 0) / todayCheckins.length).toFixed(1)
    : null;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">👥</span>
          <div>
            <div className="text-sm font-medium text-stone-800">Participant Moods</div>
            <div className="text-xs text-stone-500">
              {todayCheckins.length}/{entries.length} checked in today
              {todayAvg && <span> · Avg: {todayAvg}/5</span>}
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-amber-700 hover:text-amber-800 font-medium"
        >
          {expanded ? 'Less' : 'Details'}
        </button>
      </div>

      {/* Quick badges */}
      {todayCheckins.length > 0 && (
        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          {todayCheckins
            .sort((a, b) => a.checkins[today].rating - b.checkins[today].rating)
            .map(d => (
            <span
              key={d.user.id}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${moodColors[d.checkins[today].rating]}`}
              title={`${d.user.name}: ${moodLabels[d.checkins[today].rating]}`}
            >
              {moodEmojis[d.checkins[today].rating]} {d.user.name?.split(' ')[0]}
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div className="border-t border-stone-100 max-h-60 overflow-y-auto divide-y divide-stone-50">
          {entries.map(d => {
            const todayEntry = d.checkins[today];
            const recent = recentMoodEntries(d.checkins);
            const avg = recent.length > 0
              ? (recent.reduce((s, [, e]) => s + e.rating, 0) / recent.length).toFixed(1)
              : null;
            return (
              <div key={d.user.id} className="px-4 py-2.5 hover:bg-stone-50">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{todayEntry ? moodEmojis[todayEntry.rating] : '⬜'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-800 truncate">{d.user.name}</div>
                    <div className="text-xs text-stone-500">{avg ? `7-day avg: ${avg}/5` : 'No recent data'}</div>
                  </div>
                  {todayEntry ? (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${moodColors[todayEntry.rating]}`}>
                      {moodLabels[todayEntry.rating]}
                    </span>
                  ) : (
                    <span className="text-xs text-stone-500 italic">Not yet today</span>
                  )}
                </div>
                {/* Mini sparkline */}
                {recent.length > 1 && (
                  <div className="flex items-end gap-0.5 mt-1.5 ml-8">
                    {recent.slice().reverse().map(([date, entry]) => (
                      <div
                        key={date}
                        className={`w-3 rounded-sm ${
                          entry.rating >= 4 ? 'bg-green-300' : entry.rating === 3 ? 'bg-amber-300' :
                          entry.rating === 2 ? 'bg-orange-300' : 'bg-red-300'
                        }`}
                        style={{ height: `${entry.rating * 4}px` }}
                        title={`${date}: ${moodLabels[entry.rating]}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Confidentiality & Beta Tester Agreement View — with electronic signature capture

export { TeamMoodOverview, MoodCheckInModal, MoodHistoryWidget, ParticipantMoodOverview };
